import { Check } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type { Community } from "@/api/types";
import { useJoinCommunity } from "@/api/queries/communities";
import { Img } from "@/components/media/Img";
import { Button, Skeleton } from "@/components/ui";
import { formatCompact } from "@/lib/format";

export function CommunityRow({ community }: { community: Community }) {
  const join = useJoinCommunity();

  return (
    <div className="flex h-[72px] items-center gap-3 border-b border-border-subtle px-3 last:border-b-0 sm:px-4">
      <Img
        imageKey={community.icon.key}
        alt={community.icon.alt}
        aspect="1/1"
        sizes="48px"
        className="size-12 shrink-0 rounded-sm"
      />
      <div className="min-w-0 flex-1">
        <Link
          to="/c/$slug"
          params={{ slug: community.slug }}
          preload="intent"
          className="block truncate text-h5 hover:text-primary-text"
        >
          {community.name}
        </Link>
        <p className="truncate text-small text-text-secondary">{community.description}</p>
        <p className="text-caption tnum text-text-muted">
          {formatCompact(community.members)} members • {formatCompact(community.online)} online
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        variant={community.joined ? "secondary" : "primary"}
        leadingIcon={community.joined ? <Check size={16} weight="regular" /> : undefined}
        onClick={() => join.mutate({ slug: community.slug, joined: !community.joined })}
      >
        {community.joined ? "Joined" : "Join"}
      </Button>
    </div>
  );
}

export function CommunityRowSkeleton() {
  return (
    <div className="flex h-[72px] items-center gap-3 border-b border-border-subtle px-3 last:border-b-0 sm:px-4">
      <Skeleton className="size-12 shrink-0 rounded-sm" />
      <div className="min-w-0 flex-1 space-y-1">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-32" />
      </div>
      <Skeleton className="h-7 w-16" />
    </div>
  );
}
